import React from 'react';
import EventNotification from '../../interfaces/eventNotification/EventNotification';

export type ToastType = 'success' | 'info' | 'error' | 'warning';

export const getToastType = (notification: EventNotification): ToastType => {
    const { type } = notification;

    if (type?.includes('error') || type?.includes('failed') || type?.includes('rejected')) {
        return 'error';
    }
    if (type?.includes('success') || type?.includes('completed') || type?.includes('approved')) {
        return 'success';
    }
    if (type?.includes('warn') || type?.includes('warning') || type?.includes('attention')) {
        return 'warning';
    }
    return 'info';
};

type Resource = NonNullable<EventNotification['notificationResources']>[number];
const aliases: Record<string, string> = { author: 'user', activity: 'task_action' };
const icons: Record<string, string> = {
    task: 'fa-thumb-tack', ticket: 'fa-ticket-alt', file: 'fa-file',
    task_action: 'fa-bolt', meeting: 'fa-video', LAUNCHSURVEY: 'fa-video', FILE: 'fa-video', training_credit: 'fa-award',
};

// Keep supported web links, but never turn data or script URLs into navigation.
export const safeNotificationHref = (value: string): string | undefined => {
    if (!value || value.includes('\\') || value.startsWith('//') ||
        Array.from(value).some(character => character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127)) return undefined;
    try {
        const url = new URL(value, 'https://notification.invalid');
        return ['http:', 'https:'].includes(url.protocol) ? value : undefined;
    } catch { return undefined; }
};

const renderResource = (resource: Resource, title: boolean) => {
    if (title) return <strong>{resource.resourceName}</strong>;
    const linkable = resource.resourceType === 'user' || resource.resourceType === 'wiki_page' || Object.prototype.hasOwnProperty.call(icons, resource.resourceType);
    const href = linkable ? safeNotificationHref(resource.resourceType === 'user'
        ? `/profile${resource.resourceId}` : resource.resourceId) : undefined;
    if (!href) return <strong>{resource.resourceName}</strong>;
    return <a href={href} className="notification-resource-link">
        {icons[resource.resourceType] && <i className={`fas ${icons[resource.resourceType]}`} style={{ marginRight: '0.25rem' }} />}
        {resource.resourceType === 'user' ? <strong>{resource.resourceName}</strong> : resource.resourceName}
    </a>;
};

const interpolate = (text: string, resources: Resource[], title: boolean) => {
    const queues = new Map<string, Resource[]>();
    resources.forEach(resource => queues.set(resource.resourceType, [...(queues.get(resource.resourceType) || []), resource]));
    // React escapes both template text and resource names. Only our own nodes create markup.
    return <span>{text.split(/(\{\w+\})/g).map((part, index) => {
        const placeholder = /^\{(\w+)\}$/.exec(part)?.[1];
        const resource = placeholder ? queues.get(aliases[placeholder] || placeholder)?.shift() : undefined;
        return <React.Fragment key={index}>{resource ? renderResource(resource, title) : part}</React.Fragment>;
    })}</span>;
};

export const renderNotificationTitle = (notification: EventNotification) =>
    interpolate(notification.title || 'Notification', notification.notificationResources || [], true);

export const renderNotificationMessage = (notification: EventNotification) => {
    const resources = notification.notificationResources || [];
    let message = notification.message || '';
    const ticket = resources.find(resource => resource.resourceType === 'ticket');
    if (notification.type?.startsWith('ticket.') && ticket) {
        message = message.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
            match => ticket.resourceId.includes(match) ? '{ticket}' : match);
    }
    const wiki = resources.find(resource => resource.resourceType === 'wiki_page');
    if (wiki?.resourceName && !message.includes('{wiki_page}')) {
        message = message.replace(wiki.resourceName, () => '{wiki_page}');
    }
    return interpolate(message, resources, false);
};
